import { UniqueEntityId, WatchedList } from "@kernel/ddd";
import { Participant } from "./Participant";
import { RemoteParticipant, isRemoteParticipant } from "./RemoteParticipant";

export class ParticipantList<
  T extends Participant & { id: UniqueEntityId; clone(): T }
> extends WatchedList<T> {
  public getParticipantByRemoteId(remoteId: number): Participant | null {
    if (this.items.length === 0) return null;
    const participant = this.items.find((participant) => {
      if (!participant.remoteId) return false;
      return participant.remoteId.equals(new UniqueEntityId(remoteId));
    });
    return (participant as unknown as RemoteParticipant) || null;
  }

  public getFullfilledParticipants(): ParticipantList<Participant> {
    if (this.items.length === 0) return ParticipantList.create();
    const list = this.items.filter((participant) => {
      if (participant.stream === null) return false;
      return participant.stream.isFullfilled();
    }) as unknown as RemoteParticipant[];
    return ParticipantList.create(list);
  }

  public getPinnedParticipant(): Participant | null {
    if (this.items.length === 0) return null;
    const participant = this.items.find((participant) => {
      return participant.isPinned();
    });
    return participant || null;
  }

  public hasByRemoteId(remoteId: UniqueEntityId): boolean {
    if (this.items.length === 0) return false;
    return this.items.some((participant) => {
      if (!isRemoteParticipant(participant)) return false;
      const remoteParticipant = participant as RemoteParticipant;
      if (!remoteParticipant.remoteId) return false;
      return remoteParticipant.remoteId.equals(remoteId);
    });
  }

  public getRemoteParticipants(): ParticipantList<RemoteParticipant> {
    if (this.items.length === 0) return ParticipantList.create();
    const list = this.items.filter((participant) => {
      return isRemoteParticipant(participant);
    }) as unknown as RemoteParticipant[];
    return ParticipantList.create(list);
  }

  public compareItems(a: Participant, b: Participant): boolean {
    return a.equals(b);
  }

  public static create<
    T extends Participant & { id: UniqueEntityId; clone(): T }
  >(initialDevices?: T[]): ParticipantList<T> {
    return new ParticipantList<T>(initialDevices ? initialDevices : []);
  }
}
